/**
 * One video's transcript as plain lines (`transcript-lines/<id>/`), for the quick look's
 * transcript that follows the player. Kept for the page's life, like the clips.
 */
import { useEffect, useState } from "react";
import apiRequest from "../../../../utils/apiClient";
import { hostApi } from "../../../constants";

export interface Line {
  id: number;
  start: number;
  end: number;
  text: string;
  speaker: string;
  thumbnail: string;
}

const cache = new Map<number, Line[]>();

export const useTranscriptLines = (transcriptId: number): { lines: Line[]; loading: boolean } => {
  const [lines, setLines] = useState<Line[]>(() => cache.get(transcriptId) || []);
  const [loading, setLoading] = useState(!cache.has(transcriptId));

  useEffect(() => {
    const hit = cache.get(transcriptId);
    if (hit) { setLines(hit); setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    apiRequest(`${hostApi}transcript-lines/${transcriptId}/`, { method: "GET" })
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        const body = await res.json();
        cache.set(transcriptId, body.lines || []);
        if (!cancelled) setLines(body.lines || []);
      })
      .catch(() => { /* no transcript box content; the clips still work */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [transcriptId]);

  return { lines, loading };
};

/** The line being said at `t` seconds (the last one that started by then). Lines are in time order. */
export const lineAt = (lines: Line[], t: number): Line | undefined => {
  let lo = 0;
  let hi = lines.length - 1;
  let found: Line | undefined;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (lines[mid].start <= t + 0.05) { found = lines[mid]; lo = mid + 1; } else { hi = mid - 1; }
  }
  return found;
};

/** [word, start, end] for one stretch of a video. */
export type TimedWord = [string, number, number];
const wordCache = new Map<string, TimedWord[]>();

/** The words of the clip being said, with their times (fetched when that clip comes up). */
export const useClipWords = (transcriptId: number, span: { start: number; end: number } | null): TimedWord[] => {
  const key = span ? `${transcriptId}|${span.start}|${span.end}` : "";
  const [words, setWords] = useState<TimedWord[]>(() => (key && wordCache.get(key)) || []);
  useEffect(() => {
    if (!span) { setWords([]); return undefined; }
    const hit = wordCache.get(key);
    if (hit) { setWords(hit); return undefined; }
    let cancelled = false;
    setWords([]);
    apiRequest(`${hostApi}transcript-words/${transcriptId}/?start=${span.start}&end=${span.end}`, { method: "GET" })
      .then(async (res) => {
        if (!res.ok) return;
        const body = await res.json();
        wordCache.set(key, body.words || []);
        if (!cancelled) setWords(body.words || []);
      })
      .catch(() => { /* the line shows without word lighting */ });
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return words;
};
